import React, { useMemo, useState } from 'react';

import { AiElementsScope, ThreadSwitcher as AiThreadSwitcher } from '@modernloop/ai-elements';
import type { ThreadSwitcherGroup } from '@modernloop/ai-elements';
import { useTranslation } from '@modernloop/i18n';
import { useTheme } from '@mui/material/styles';

import useDisplayTimezoneValue from 'src/hooks/useDisplayTimezoneValue';

import EditableAgentThreadTitle, { EditableAgentThreadTitleView } from './EditableAgentThreadTitle';
import {
  AGENT_THREAD_GROUP,
  getAgentThreadPresentationGroups,
  getAgentThreadRelativeAge,
} from './agent/agentThreadPresentation';
import type { AgentThreadGroup } from './agent/agentThreadPresentation';
import type { AgentThreadSummary } from './agent/protocol';
import { muiThemeToAiTokens } from './aiElementsTheme';

type Props = {
  threads: readonly AgentThreadSummary[];
  activeThreadId: string;
  onSelectThread: (threadId: string) => void;
  onNewThread: () => void;
  /** True while the active conversation holds no messages yet. */
  isEmptyThread?: boolean;
  /** Optional compact trigger presentation for surfaces such as the bottom dock. */
  singleTrigger?: boolean;
  triggerClassName?: string;
  triggerLabel?: string;
  triggerTestId?: string;
  triggerTitle?: string;
  dependencies?: {
    locale: string;
    renameThread: Parameters<typeof EditableAgentThreadTitleView>[0]['renameThread'];
    timezone: string;
    translate: (key: string, values?: Record<string, string | number>) => string;
  };
};

/**
 * The single Agent workspace control for finding, resuming, or starting a conversation.
 *
 * This container owns only what the presentation component cannot know: translated copy, the
 * display timezone, and the search text that drives grouping. Rendering, keyboard behavior, and
 * popover state belong to the AI Elements `ThreadSwitcher`.
 */
const ThreadSwitcher: React.FC<Props> = ({
  threads,
  activeThreadId,
  onSelectThread,
  onNewThread,
  isEmptyThread = false,
  singleTrigger = false,
  triggerClassName,
  triggerLabel,
  triggerTestId = 'agent-new-chat-trigger',
  triggerTitle,
  dependencies,
}) => {
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const tokens = useMemo(() => muiThemeToAiTokens(theme), [theme]);
  const displayTimezone = useDisplayTimezoneValue();
  const locale = dependencies?.locale ?? i18n.language;
  const timezone = dependencies?.timezone ?? displayTimezone;
  const translate = dependencies?.translate ?? ((key, values) => t(key, values));
  const [search, setSearch] = useState('');
  const untitled = translate('hiring-dashboard-ai-assistant:agent.threads.untitled');
  const now = new Date();
  const headingForGroup = (group: AgentThreadGroup): string | undefined => {
    switch (group) {
      case AGENT_THREAD_GROUP.TODAY:
        // The leading group needs no heading: the newest conversations are the expected default.
        return undefined;
      case AGENT_THREAD_GROUP.YESTERDAY:
        return translate('common:date.yesterday');
      case AGENT_THREAD_GROUP.OLDER:
        return translate('hiring-dashboard-ai-assistant:agent.threads.older');
    }
  };
  // Grouping is pure derived render state keyed off the current clock, so memoizing it would
  // only add a dependency list that can never be satisfied.
  const groups: readonly ThreadSwitcherGroup[] = getAgentThreadPresentationGroups(threads, {
    locale,
    now,
    search,
    timezone,
  }).map((group) => ({
    heading: headingForGroup(group.type),
    id: group.type,
    items: group.threads.map((thread) => {
      const title = thread.title ?? untitled;
      const age = getAgentThreadRelativeAge(thread.updatedAt, now);
      const ageText = age ? translate(`common:relativeTime.${age.key}`, { val: age.value }) : '';
      return {
        ariaLabel: ageText
          ? translate('hiring-dashboard-ai-assistant:agent.threads.itemLabel', {
              age: ageText,
              title,
            })
          : title,
        id: thread.externalThreadId,
        meta: ageText,
        title,
      };
    }),
  }));
  // A conversation with no messages has nothing to name yet, so it reads as the new-chat label
  // and stops acting as a second way into the history.
  const activeTitle =
    triggerTitle ??
    (isEmptyThread
      ? translate('hiring-dashboard-ai-assistant:agent.threads.new')
      : (threads.find((thread) => thread.externalThreadId === activeThreadId)?.title ?? untitled));

  return (
    <AiElementsScope tokens={tokens}>
      <AiThreadSwitcher
        activeId={activeThreadId}
        activeTitle={activeTitle}
        className={triggerClassName}
        groups={groups}
        isTitleInteractive={!isEmptyThread}
        labels={{
          empty: translate('hiring-dashboard-ai-assistant:agent.threads.empty'),
          history: translate('hiring-dashboard-ai-assistant:agent.threads.history'),
          newThread: translate('hiring-dashboard-ai-assistant:agent.threads.new'),
          search: translate('hiring-dashboard-ai-assistant:agent.threads.search'),
          searchEmpty: translate('hiring-dashboard-ai-assistant:agent.threads.searchEmpty'),
          trigger: triggerLabel ?? translate('hiring-dashboard-ai-assistant:agent.threads.switch'),
        }}
        onNewThread={onNewThread}
        onSearchChange={setSearch}
        onSelect={onSelectThread}
        search={search}
        singleTrigger={singleTrigger}
        menuTestId="agent-thread-menu-trigger"
        triggerTestId={triggerTestId}
        titleControl={
          !singleTrigger && !isEmptyThread ? (
            dependencies ? (
              <EditableAgentThreadTitleView
                key={activeThreadId}
                className="max-w-64 min-w-0 rounded-l-md border border-border bg-background px-3 text-left text-sm font-normal hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                renameThread={dependencies.renameThread}
                testId={triggerTestId}
                threadId={activeThreadId}
                title={activeTitle}
                translate={translate}
              />
            ) : (
              <EditableAgentThreadTitle
                key={activeThreadId}
                className="max-w-64 min-w-0 rounded-l-md border border-border bg-background px-3 text-left text-sm font-normal hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                testId={triggerTestId}
                threadId={activeThreadId}
                title={activeTitle}
              />
            )
          ) : undefined
        }
      />
    </AiElementsScope>
  );
};

export default ThreadSwitcher;
